/**
 * CollateralBaselinesConfig - Aaim collateral policy baselines, read-only
 *
 * Shows the baselines platform-admin-api seeds for each charter type x
 * regulatory framework (collateral-policy.openapi.yaml BaselinePolicy). This
 * console has no baseline editor: the PUT /collateral-policy/baselines route
 * exists, and nothing on this page calls it.
 *
 * Every cell of the grid is read, so a cell says what the service answered:
 * a baseline, no baseline for that combination, or a read that failed. A
 * failed read is never reported as "not seeded".
 *
 * Route: /platform-config/collateral-baselines
 * Access: AAIM_SUPER_ADMIN only
 */

import { useState, type ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Alert, BodyText, Card, Heading, PageHeader, Table } from '@aaimplatform/ui';
import { Loader2 } from 'lucide-react';
import {
  collateralPolicyApi,
  type AssetClassPolicy,
  type BaselineCharterType,
  type BaselineFramework,
  type BaselinePolicy,
} from '../../api/services/collateral-policy';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { formatRatioAsPercent } from '../../utils/display-format';

const CHARTER_TYPES: readonly BaselineCharterType[] = ['CREDIT_UNION', 'COMMUNITY_BANK', 'REGIONAL_BANK'];
const REGULATORY_FRAMEWORKS: readonly BaselineFramework[] = ['NCUA', 'REG_T', 'REG_U', 'OCC'];

const CHARTER_LABELS: Record<BaselineCharterType, string> = {
  CREDIT_UNION: 'Credit Union',
  COMMUNITY_BANK: 'Community Bank',
  REGIONAL_BANK: 'Regional Bank',
};

const FRAMEWORK_LABELS: Record<BaselineFramework, string> = {
  NCUA: 'NCUA',
  REG_T: 'Reg T',
  REG_U: 'Reg U',
  OCC: 'OCC',
};

const SOURCE_LABELS: Record<BaselinePolicy['source'], string> = {
  REGULATORY: 'Regulatory frameworks',
  NETWORK_EMPIRICAL: 'Network performance data',
  HYBRID: 'Regulatory floor with network adjustments',
};

const ALERT_CHANNEL_LABELS: Record<components['schemas']['AlertChannel'], string> = {
  EMAIL: 'Email',
  WEBHOOK: 'Webhook',
  SMS: 'SMS',
  PORTAL: 'Portal',
};

const RED_TIER_HANDOFF_LABELS: Record<components['schemas']['RedTierHandoff'], string> = {
  FI_REVIEW_QUEUE: 'FI review queue',
  FI_CONTACT_MEMBER: 'FI contacts the member',
};

const REPORTING_FREQUENCY_LABELS: Record<components['schemas']['ReportingFrequency'], string> = {
  REAL_TIME: 'Real time',
  DAILY_DIGEST: 'Daily digest',
  WEEKLY_SUMMARY: 'Weekly summary',
};

type SelectedCellState = { charter: BaselineCharterType; framework: BaselineFramework };

const CELLS: readonly SelectedCellState[] = CHARTER_TYPES.flatMap((charter) =>
  REGULATORY_FRAMEWORKS.map((framework) => ({ charter, framework })),
);

/**
 * One row of the matrix per charter type. The charter label is an ordinary
 * first-column cell of the kit Table: a raw `<th scope="row">` picked up the
 * preset's table-header treatment (uppercase, wide tracking) and read as a
 * second header column.
 */
const CHARTER_ROWS = CHARTER_TYPES.map((charter) => ({ charter, label: CHARTER_LABELS[charter] }));

function percentOrAbsent(ratio: number | undefined): string {
  return typeof ratio === 'number' ? formatRatioAsPercent(ratio) : 'Not set';
}

/**
 * The per-asset-class table of one baseline: eligibility, the LTV cap, the
 * haircut (discountRate), and the Collateral Health tier ceilings.
 */
function AssetClassPolicyTable({ policies }: { policies: AssetClassPolicy[] }) {
  if (policies.length === 0) {
    return (
      <BodyText variant="ui" size="sm" color="secondary">
        This baseline carries no asset class policies.
      </BodyText>
    );
  }
  return (
    <Table
      label="Baseline asset class policies"
      hoverable={false}
      columns={[
        { key: 'displayName', label: 'Asset class' },
        {
          key: 'enabled',
          label: 'Eligible',
          render: (value: boolean) => (value ? 'Eligible' : 'Not eligible'),
        },
        { key: 'maxLtv', label: 'Max LTV', align: 'right', render: (v: number) => percentOrAbsent(v) },
        { key: 'discountRate', label: 'Haircut', align: 'right', render: (v: number) => percentOrAbsent(v) },
        {
          key: 'chaThresholds',
          label: 'Health tiers (green / yellow / orange / red)',
          align: 'right',
          render: (_v: unknown, row: AssetClassPolicy) =>
            [
              row.chaThresholds.targetLtv,
              row.chaThresholds.yellowLtv,
              row.chaThresholds.orangeLtv,
              row.chaThresholds.redLtv,
            ]
              .map(percentOrAbsent)
              .join(' / '),
        },
      ]}
      data={policies}
    />
  );
}

function BaselineDetail({ baseline }: { baseline: BaselinePolicy }) {
  const concentration = baseline.concentrationPolicy;
  const monitoring = baseline.monitoringDefaults;
  return (
    <div className="mt-4 space-y-6">
      <BodyText variant="ui" size="sm" color="secondary">
        Source: {SOURCE_LABELS[baseline.source]}. Confidence:{' '}
        {typeof baseline.confidence === 'number'
          ? formatRatioAsPercent(baseline.confidence)
          : 'not calibrated'}.
      </BodyText>

      <div>
        <Heading level={4} size="compact" className="mb-3">
          Asset class policies
        </Heading>
        <AssetClassPolicyTable policies={baseline.assetClassPolicies} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Heading level={4} size="compact" className="mb-3">
            Concentration limits
          </Heading>
          <dl className="space-y-2 text-sm font-sans">
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Single position</dt>
              <dd className="font-mono tabular-nums text-primary">{percentOrAbsent(concentration.maxSinglePositionPct)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Single issuer</dt>
              <dd className="font-mono tabular-nums text-primary">{percentOrAbsent(concentration.maxSingleIssuerPct)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Single sector</dt>
              <dd className="font-mono tabular-nums text-primary">{percentOrAbsent(concentration.maxSingleSectorPct)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Single asset class</dt>
              <dd className="font-mono tabular-nums text-primary">{percentOrAbsent(concentration.maxSingleAssetClassPct)}</dd>
            </div>
          </dl>
        </div>
        <div>
          <Heading level={4} size="compact" className="mb-3">
            Monitoring defaults
          </Heading>
          <dl className="space-y-2 text-sm font-sans">
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Alert channels</dt>
              <dd className="text-primary text-right">
                {monitoring.alertChannels.length > 0
                  ? monitoring.alertChannels.map((c) => ALERT_CHANNEL_LABELS[c]).join(', ')
                  : 'None'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Red tier handoff</dt>
              <dd className="text-primary text-right">{RED_TIER_HANDOFF_LABELS[monitoring.redTierHandoff]}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary">Reporting</dt>
              <dd className="text-primary text-right">{REPORTING_FREQUENCY_LABELS[monitoring.reportingFrequency]}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

export function CollateralBaselinesConfigPage() {
  const [selectedCell, setSelectedCell] = useState<SelectedCellState | null>(null);

  const cellQueries = useQueries({
    queries: CELLS.map((cell) => ({
      queryKey: ['baseline', cell.charter, cell.framework],
      queryFn: () => collateralPolicyApi.getBaseline(cell.charter, cell.framework),
    })),
  });

  const allSettled = cellQueries.every((q) => !q.isLoading);
  const firstError = cellQueries.find((q) => q.isError)?.error;
  const seededCount = cellQueries.filter((q) => q.data).length;
  const noneSeeded = allSettled && !firstError && seededCount === 0;

  const queryFor = (cell: SelectedCellState) =>
    cellQueries[CELLS.findIndex((c) => c.charter === cell.charter && c.framework === cell.framework)];

  const selectedQuery = selectedCell ? queryFor(selectedCell) : undefined;

  const cellContent = (charter: BaselineCharterType, fw: BaselineFramework): ReactNode => {
    const cell = { charter, framework: fw };
    const query = queryFor(cell);
    const isSelected = selectedCell?.charter === charter && selectedCell?.framework === fw;
    if (query.isLoading) {
      return <span className="text-xs text-tertiary">Loading</span>;
    }
    if (query.isError) {
      return <span className="text-xs text-error">Unavailable</span>;
    }
    if (query.data) {
      return (
        <button
          type="button"
          onClick={() => setSelectedCell(cell)}
          aria-pressed={isSelected}
          aria-label={`View ${CHARTER_LABELS[charter]} ${FRAMEWORK_LABELS[fw]} baseline`}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            isSelected
              ? 'bg-selection/10 text-primary border border-selection'
              : 'bg-elevated hover:bg-recessed text-secondary border border-transparent'
          }`}
        >
          View
        </button>
      );
    }
    return <span className="text-xs text-tertiary">Not defined</span>;
  };

  return (
    <div>
      <PageHeader description="Aaim's recommended starting collateral policy for a financial institution, keyed by charter type and regulatory framework. Read-only: baselines are seeded by platform-admin-api, and this console has no baseline editor." />
      <div className="space-y-6">

      {firstError && (
        <Alert type="error" title="Baselines could not be loaded">
          {firstError instanceof Error ? firstError.message : 'The baseline read failed.'}{' '}
          A failed read says nothing about whether baselines are seeded.
        </Alert>
      )}

      {noneSeeded && (
        <Alert type="warning" title="No baselines seeded">
          The service answered that no baseline exists for any charter type and
          framework. Baselines are seeded on platform-admin-api startup; check
          its logs.
        </Alert>
      )}

      {/* Grid -- read-only, select a cell to view its detail below. The kit
          Table scrolls sideways inside the card on a phone. */}
      <Card className="p-0">
        <Table
          label="Collateral baselines"
          hoverable={false}
          columns={[
            { key: 'label', label: 'Charter type' },
            ...REGULATORY_FRAMEWORKS.map((fw) => ({
              key: fw,
              label: FRAMEWORK_LABELS[fw],
              align: 'center' as const,
              render: (_value: unknown, row: (typeof CHARTER_ROWS)[number]) => cellContent(row.charter, fw),
            })),
          ]}
          data={CHARTER_ROWS}
        />
      </Card>

      {/* Selected baseline detail */}
      {selectedCell && selectedQuery && (
        <Card className="p-6">
          <Heading level={3} size="card">
            {CHARTER_LABELS[selectedCell.charter]} / {FRAMEWORK_LABELS[selectedCell.framework]}
          </Heading>
          {selectedQuery.isLoading ? (
            <div className="flex items-center gap-2 mt-4 text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <BodyText variant="ui" size="sm">Loading baseline...</BodyText>
            </div>
          ) : selectedQuery.data ? (
            <BaselineDetail baseline={selectedQuery.data} />
          ) : (
            <BodyText variant="ui" size="sm" color="secondary" className="mt-4">
              No baseline is defined for this combination.
            </BodyText>
          )}
        </Card>
      )}
      </div>
    </div>
  );
}
